import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { capitalize } from 'lodash';

import { ENTITY_TYPE_TO_TEXT_MAP } from '@neptune/entity-common-domain';
import { RenameInput } from '@neptune/shared/common-ui';
import { EntityType } from '@neptune/shared/entity-domain';
import { ActionsMenu, Banner, bemBlock, Icon, SimpleTooltip, Text } from '@neptune/shared/venus-ui';

import config from 'config';

import './DetailsDropdownMenuContent.less';

const block = bemBlock('details-dropdown-menu-content');

type Props = {
  align?: React.ComponentProps<typeof ActionsMenu.Content>['align'];
  side?: React.ComponentProps<typeof ActionsMenu.Content>['side'];
  namePlaceholder?: string;
  name?: string;
  colorInput?: React.ReactNode;
  isSaving?: boolean;
  entityType: EntityType;
  disableInput?: boolean;
  onShowDetails: () => void;
  onStop?: () => void;
  onRestore?: () => void;
  onAbort?: () => void;
  onManageTags?: () => void;
  onRemove?: () => void;
  onCopy?: () => void;
  onCopyAlias?: () => void;
  onOpenYoda?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onRename?: (name: string) => void;
  onShowAssociatedRuns?: () => void;
  onRenameEnter?: () => void;
};

export const DetailsDropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenu.Content>,
  Props
>(function DetailsDropdownMenuContent(
  {
    onCopy,
    onCopyAlias,
    onOpenYoda,
    colorInput,
    align = 'start',
    side = 'right',
    entityType,
    onShowDetails,
    onStop,
    onRestore,
    onAbort,
    onManageTags,
    onShowAssociatedRuns,
    onRemove,
    onArchive,
    onUnarchive,
    onRename,
    disableInput,
    isSaving,
    namePlaceholder,
    name = '',
    onRenameEnter,
  },
  ref,
) {
  const inputProps = React.useMemo(
    () => ({
      placeholder: namePlaceholder,
    }),
    [namePlaceholder],
  );

  return (
    <ActionsMenu.Content ref={ref} align={align} side={side} data-role="details-dropdown-menu">
      {onRename && (
        <>
          <ActionsMenu.Slot>
            <Banner.Info className={block('banner')} size="small">
              {capitalize(ENTITY_TYPE_TO_TEXT_MAP[entityType])} will be renamed in this report only
            </Banner.Info>
          </ActionsMenu.Slot>
          <SimpleTooltip
            triggerClassName={block('name-input-tooltip')}
            content={disableInput ? 'Alias can only be changed in Editing mode' : undefined}
          >
            <ActionsMenu.FocusableItem
              containerClassName={block('name-input-slot-item')}
              focusOnMouseEnter={!disableInput}
            >
              <RenameInput
                className={block('name-input')}
                name={name}
                onRename={onRename}
                onEnter={onRenameEnter}
                isSaving={isSaving}
                inputProps={inputProps}
                disabled={disableInput}
                autoFocus
              />
            </ActionsMenu.FocusableItem>
          </SimpleTooltip>
          <ActionsMenu.Separator />
        </>
      )}
      {onCopy && (
        <ActionsMenu.Item
          leadingIcon={<Icon glyph="clone" />}
          onClick={onCopy}
          data-item={`copy-${entityType}-name`}
        >
          <Text>
            {onCopyAlias
              ? `Copy original ${ENTITY_TYPE_TO_TEXT_MAP[entityType]} name`
              : `Copy ${ENTITY_TYPE_TO_TEXT_MAP[entityType]} name`}
          </Text>
        </ActionsMenu.Item>
      )}
      {onCopyAlias && (
        <ActionsMenu.Item
          leadingIcon={<Icon glyph="clone" />}
          onClick={onCopyAlias}
          data-item={`copy-${entityType}-alias-name`}
        >
          <Text>Copy aliased {ENTITY_TYPE_TO_TEXT_MAP[entityType]} name</Text>
        </ActionsMenu.Item>
      )}
      {colorInput}
      <ActionsMenu.Item
        leadingIcon={<Icon glyph="info-circle" />}
        data-item={`show-${entityType}-details`}
        onClick={onShowDetails}
      >
        <Text>{`${capitalize(ENTITY_TYPE_TO_TEXT_MAP[entityType])} information`}</Text>
      </ActionsMenu.Item>
      {onOpenYoda && (
        <ActionsMenu.Item
          leadingIcon={<Icon glyph="external-link" />}
          data-item="open-yoda"
          onClick={onOpenYoda}
        >
          <Text>Open Yoda</Text>
        </ActionsMenu.Item>
      )}
      {onStop && (
        <SimpleTooltip
          content={
            config.disableRemoteRunActions
              ? "Remote stop isn't available in your installation yet"
              : undefined
          }
        >
          <ActionsMenu.Item
            leadingIcon={<Icon glyph="pause-circle" />}
            data-item="remote-stop"
            disabled={config.disableRemoteRunActions}
            onClick={() => {
              onStop();
            }}
          >
            Remote stop
          </ActionsMenu.Item>
        </SimpleTooltip>
      )}
      {onRestore && (
        <ActionsMenu.Item
          leadingIcon={<Icon glyph="undo" />}
          data-item="restore"
          onClick={() => {
            onRestore();
          }}
        >
          Restore
        </ActionsMenu.Item>
      )}
      {onAbort && (
        <SimpleTooltip
          content={
            config.disableRemoteRunActions
              ? "Remote abort isn't available in your installation yet"
              : undefined
          }
        >
          <ActionsMenu.Item
            leadingIcon={<Icon glyph="crossed-circle" />}
            data-item="remote-abort"
            disabled={config.disableRemoteRunActions}
            onClick={() => {
              onAbort();
            }}
          >
            Remote abort
          </ActionsMenu.Item>
        </SimpleTooltip>
      )}
      {onManageTags && (
        <ActionsMenu.Item
          leadingIcon={<Icon glyph="tag" />}
          data-item="manage-tags"
          onClick={onManageTags}
        >
          Manage tags
        </ActionsMenu.Item>
      )}
      {onShowAssociatedRuns && (
        <ActionsMenu.Item
          data-item="see-associated-runs"
          leadingIcon={<Icon glyph="leaf-runs" />}
          onClick={onShowAssociatedRuns}
        >
          See associated runs
        </ActionsMenu.Item>
      )}
      {onRemove && (
        <SimpleTooltip
          content={
            config.withDisabledTrashing
              ? "Trashing isn't available in your installation yet"
              : undefined
          }
        >
          <ActionsMenu.Item
            leadingIcon={<Icon glyph="trash" />}
            data-item="trash"
            disabled={config.withDisabledTrashing}
            onClick={onRemove}
          >
            Move to trash
          </ActionsMenu.Item>
        </SimpleTooltip>
      )}
      {onArchive && (
        <ActionsMenu.Item
          data-item={`archive-${entityType}`}
          onClick={() => {
            onArchive();
          }}
        >
          <Text color="error">Archive {ENTITY_TYPE_TO_TEXT_MAP[entityType]}</Text>
        </ActionsMenu.Item>
      )}
      {onUnarchive && (
        <ActionsMenu.Item
          data-item={`unarchive-${entityType}`}
          onClick={() => {
            onUnarchive();
          }}
        >
          <Text color="text-action">Restore {ENTITY_TYPE_TO_TEXT_MAP[entityType]}</Text>
        </ActionsMenu.Item>
      )}
    </ActionsMenu.Content>
  );
});
